import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../../services/supabaseClient';
import { useAuth } from '../../../context/AuthContext';
import RexiPopup from '../../../components/RexiPopup';
import SmartPagination from '../../../components/SmartPagination';
import '../DataTableTemplate.css';
import './Factories.css';
import AddEditFactoryModal from "./AddEditFactoryModal";

interface Factory {
  id: number;
  name: string;
  address_line_1?: string;
  address_line_2?: string;
  suite?: string;
  city?: string;
  state_province?: string;
  country?: string;
  postal_code?: string;
  telephone?: string;
  email?: string;
  status: 'active' | 'inactive';
  inserted_at: string;
  updated_at: string;
}

// Moved outside component to prevent recreation on every render
const FilterInput = ({ label, placeholder, column, value, onChange }: {
  label: string;
  placeholder: string;
  column: string;
  value: string;
  onChange: (column: string, value: string) => void;
}) => (
  <div className="filter-group">
    <label className="filter-label">{label}</label>
    <input
      type="text"
      className="filter-input"
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(column, e.target.value)}
    />
  </div>
);

const Factories = () => {
  const { user } = useAuth();
  const [factories, setFactories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [columnFilters, setColumnFilters] = useState({
    name: '',
    address: '',
    city: '',
    state_province: '',
    country: '',
    postal_code: '',
    phone: '',
    email: '',
    status: 'all'
  });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [popupState, setPopupState] = useState({
    help: false,
    delete: false,
    modal: false,
    foreignKeyError: false
  });
  const [factoryToDelete, setFactoryToDelete] = useState(null);

  const [editingFactory, setEditingFactory] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [formData, setFormData] = useState({
    name: '',
    address_line_1: '',
    address_line_2: '',
    suite: '',
    city: '',
    state_province: '',
    country: '',
    postal_code: '',
    telephone: '',
    email: '',
    status: 'active' as 'active' | 'inactive'
  });
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Fetch factories from Supabase
  const fetchFactories = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('factories')
        .select('*')
        .eq('auth_id', user?.id)
        .order('name', { ascending: true });

      if (error) {
        throw error;
      }

      setFactories(data || []);
    } catch (err: any) {
      setError(err.message);
      console.error('Error fetching factories:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFactories();
  }, []);

  // Helper functions for popup management
  const togglePopup = (popup: keyof typeof popupState, show: boolean) => {
    setPopupState((prev: typeof popupState) => ({ ...prev, [popup]: show }));
  };

  // Table configuration - drives both headers and data rendering
  const tableColumns = [
    { key: 'name', label: 'Factory Name', sortable: false, className: 'name', showInTable: true },
    { key: 'address_line_1', label: 'Address Line 1', sortable: false, showInTable: false }, // Hidden but available for filtering
    { key: 'address_line_2', label: 'Address Line 2', sortable: false, showInTable: false }, // Hidden but available for filtering
    { key: 'suite', label: 'Suite', sortable: false, showInTable: false }, // Only for CSV
    { key: 'city', label: 'City', sortable: false, className: 'city', showInTable: true },
    { key: 'state_province', label: 'State/Province', sortable: false, className: 'state', showInTable: true },
    { key: 'country', label: 'Country', sortable: true, className: 'country', showInTable: true },
    { key: 'postal_code', label: 'Postal Code', sortable: false, className: 'postal', showInTable: false }, // Hidden but available for filtering
    { key: 'telephone', label: 'Phone', sortable: false, className: 'phone', showInTable: false }, // Hidden but available for filtering
    { key: 'email', label: 'Email', sortable: false, className: 'email', showInTable: false }, // Hidden but available for filtering
    { key: 'status', label: 'Status', sortable: true, className: 'status', showInTable: true },
    { key: 'actions', label: 'Actions', sortable: false, className: 'actions', showInTable: true }
  ];

  // Filter factories based on column filters
  const filteredFactories = factories.filter((factory: Factory) => {
    const matchesName = !columnFilters.name ||
      factory.name?.toLowerCase().includes(columnFilters.name.toLowerCase());

    const matchesAddress = !columnFilters.address ||
      (factory.address_line_1?.toLowerCase().includes(columnFilters.address.toLowerCase()) ||
        factory.address_line_2?.toLowerCase().includes(columnFilters.address.toLowerCase()));

    const matchesCity = !columnFilters.city ||
      factory.city?.toLowerCase().includes(columnFilters.city.toLowerCase());

    const matchesStateProvince = !columnFilters.state_province ||
      factory.state_province?.toLowerCase().includes(columnFilters.state_province.toLowerCase());

    const matchesCountry = !columnFilters.country ||
      factory.country?.toLowerCase().includes(columnFilters.country.toLowerCase());

    const matchesPostalCode = !columnFilters.postal_code ||
      factory.postal_code?.toLowerCase().includes(columnFilters.postal_code.toLowerCase());

    const matchesPhone = !columnFilters.phone ||
      factory.telephone?.toLowerCase().includes(columnFilters.phone.toLowerCase());

    const matchesEmail = !columnFilters.email ||
      factory.email?.toLowerCase().includes(columnFilters.email.toLowerCase());

    const matchesStatus = columnFilters.status === 'all' || factory.status === columnFilters.status;

    return matchesName && matchesAddress && matchesCity && matchesStateProvince &&
      matchesCountry && matchesPostalCode && matchesPhone && matchesEmail &&
      matchesStatus;
  });

  const handleColumnFilterChange = (column: string, value: string) => {
    setColumnFilters((prev: typeof columnFilters) => ({ ...prev, [column]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const clearAllFilters = () => {
    setColumnFilters({
      name: '',
      address: '',
      city: '',
      state_province: '',
      country: '',
      postal_code: '',
      phone: '',
      email: '',
      status: 'all'
    });
    setCurrentPage(1); // Reset to first page when clearing filters
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Reusable interactive cell component
  const InteractiveCell = ({ children, className = "", dataLabel = "" }: any) => (
    <div className={`table-cell ${className}`} data-label={dataLabel}>
      <div
        className="table-cell-input"
        contentEditable
        suppressContentEditableWarning={true}
        onInput={(e: any) => e.preventDefault()}
        onKeyDown={(e: any) => {
          const allowedKeys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'Tab', 'Escape'];
          const isCopyOperation = e.ctrlKey && (e.key === 'c' || e.key === 'a');
          if (!allowedKeys.includes(e.key) && !isCopyOperation) {
            e.preventDefault();
          }
        }}
        onPaste={(e: any) => e.preventDefault()}
        onMouseDown={(e: any) => e.stopPropagation()}
        onBlur={(e: any) => e.currentTarget.scrollLeft = 0}
      >
        {children}
      </div>
    </div>
  );


  // Reusable table header cell component
  const TableHeaderCell = ({ column }: any) => (
    <div className={`table-cell ${column.className || ''}`}>
      {column.sortable ? (
        <div className="sortable-header" onClick={() => handleSort(column.key)}>
          {column.label}
          <span className={`sort-icon ${getSortIcon(column.key)} ${sortConfig.key === column.key ? 'active' : ''}`}></span>
        </div>
      ) : (
        column.label
      )}
    </div>
  );

  // Reusable table data cell component
  const TableDataCell = ({ column, factory }: any) => {
    if (column.key === 'actions') {
      return (
        <div className="table-cell" data-label="Actions">
          <div className="table-actions">
            <button className="table-action-btn table-edit-btn" onClick={() => openEditModal(factory)}>
              Details
            </button>
            <button className="table-action-btn table-delete-btn" onClick={() => confirmDelete(factory)}>
              Delete
            </button>
          </div>
        </div>
      );
    }

    if (column.key === 'status') {
      return (
        <div className="table-cell status" data-label="Status">
          {factory.status.charAt(0).toUpperCase() + factory.status.slice(1)}
        </div>
      );
    }

    // Regular interactive cell
    return (
      <InteractiveCell className={column.className || ''} dataLabel={column.label}>
        {(factory as any)[column.key] || '-'}
      </InteractiveCell>
    );
  };

  const hasActiveFilters = Object.values(columnFilters).some(filter => filter && filter !== 'all');

  // Sorting functionality
  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Apply sorting to filtered results
  const sortedFactories = useMemo(() => {
    if (!sortConfig.key) return filteredFactories;

    return [...filteredFactories].sort((a, b) => {
      let aValue: string | number = '';
      let bValue: string | number = '';

      if (sortConfig.key === 'address') {
        aValue = [a.address_line_1, a.city].filter(Boolean).join(', ').toLowerCase();
        bValue = [b.address_line_1, b.city].filter(Boolean).join(', ').toLowerCase();
      } else if (sortConfig.key === 'inserted_at' || sortConfig.key === 'updated_at') {
        aValue = new Date(a[sortConfig.key]).getTime();
        bValue = new Date(b[sortConfig.key]).getTime();
      } else {
        aValue = (a[sortConfig.key] || '').toString().toLowerCase();
        bValue = (b[sortConfig.key] || '').toString().toLowerCase();
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredFactories, sortConfig]);

  // Apply pagination to sorted results
  const paginatedFactories = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedFactories.slice(startIndex, endIndex);
  }, [sortedFactories, currentPage, itemsPerPage]);

  // Calculate pagination info
  const totalPages = Math.ceil(sortedFactories.length / itemsPerPage);
  const totalItems = sortedFactories.length;

  const getSortIcon = (columnKey: string) => {
    if (sortConfig.key !== columnKey) {
      return 'none';
    }
    return sortConfig.direction;
  };

  // Handle delete factory
  const handleDeleteFactory = async () => {
    if (!factoryToDelete) return;

    try {
      const { error } = await supabase
        .from('factories')
        .delete()
        .eq('id', factoryToDelete.id)
        .eq('auth_id', user?.id);

      if (error) {
        // Check if it's a foreign key constraint error
        if (error.message.includes('violates foreign key constraint') &&
          error.message.includes('test_reports_factory_id_fkey')) {
          // Close delete confirmation popup
          togglePopup('delete', false);
          setFactoryToDelete(null);
          // Show custom foreign key error popup
          togglePopup('foreignKeyError', true);
          return;
        }
        throw error;
      }

      setFactories(factories.filter((f: Factory) => f.id !== factoryToDelete.id));
      togglePopup('delete', false);
      setFactoryToDelete(null);
    } catch (err: any) {
      setError(err.message);
      console.error('Error deleting factory:', err);
    }
  };

  const confirmDelete = (factory: Factory) => {
    setFactoryToDelete(factory);
    togglePopup('delete', true);
  };

  // Modal functions
  const openAddModal = () => {
    setEditingFactory(null);
    setFormData({
      name: '',
      address_line_1: '',
      address_line_2: '',
      suite: '',
      city: '',
      state_province: '',
      country: '',
      postal_code: '',
      telephone: '',
      email: '',
      status: 'active'
    });
    setFormErrors({});
    togglePopup('modal', true);
  };

  const openEditModal = (factory: Factory) => {
    setEditingFactory(factory);
    setFormData({
      name: factory.name || '',
      address_line_1: factory.address_line_1 || '',
      address_line_2: factory.address_line_2 || '',
      suite: factory.suite || '',
      city: factory.city || '',
      state_province: factory.state_province || '',
      country: factory.country || '',
      postal_code: factory.postal_code || '',
      telephone: factory.telephone || '',
      email: factory.email || '',
      status: factory.status
    });
    setFormErrors({});
    togglePopup('modal', true);
  };

  const closeModal = () => {
    togglePopup('modal', false);
    setEditingFactory(null);
    setFormErrors({});
    setSaving(false);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev: typeof formData) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev: Record<string, string>) => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    // Required fields validation
    const requiredFields = [
      { field: 'name', label: 'Factory name' },
      { field: 'address_line_1', label: 'Address line 1' },
      { field: 'city', label: 'City' },
      { field: 'state_province', label: 'State/Province' },
      { field: 'country', label: 'Country' },
      { field: 'telephone', label: 'Phone number' },
      { field: 'email', label: 'Email' }
    ];

    requiredFields.forEach(({ field, label }) => {
      if (!formData[field]?.trim()) {
        errors[field] = `${label} is required`;
      }
    });

    // Email format validation
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveFactory = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      if (editingFactory) {
        // Update existing factory
        const { error } = await supabase
          .from('factories')
          .update({
            ...formData,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingFactory.id)
          .eq('auth_id', user?.id);

        if (error) throw error;

        setFactories(factories.map((f: Factory) =>
          f.id === editingFactory.id
            ? { ...f, ...formData, updated_at: new Date().toISOString() }
            : f
        ));
      } else {
        // Create new factory
        const { data, error } = await supabase
          .from('factories')
          .insert([{
            ...formData,
            auth_id: user?.id
          }])
          .select()
          .single();

        if (error) throw error;

        setFactories([...factories, data]);
      }

      closeModal();
    } catch (err: any) {
      setError(err.message);
      console.error('Error saving factory:', err);
    } finally {
      setSaving(false);
    }
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'active': return 'status-active';
      case 'pending': return 'status-pending';
      case 'inactive': return 'status-inactive';
      default: return 'status-active';
    }
  };


  const handleExportCSV = () => {
    // Use filtered factories (what's currently shown on page)
    const dataToExport = sortedFactories;

    if (dataToExport.length === 0) {
      alert('No data to export');
      return;
    }

    // Define headers from table configuration (excluding actions)
    const csvColumns = tableColumns.filter(col => col.key !== 'actions');
    const headers = csvColumns.map(col => col.label);

    // Convert data to CSV format using table configuration
    const csvContent = [
      headers.join(','),
      ...dataToExport.map((factory: Factory) =>
        csvColumns.map(col => `"${(factory as any)[col.key] || ''}"`)
          .join(',')
      )
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `factories_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="data-table-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="data-table-container">
      <h1 className="data-table-title">Factories</h1>
      <div className="page-help-link" onClick={() => togglePopup('help', true)}>
        Ask Rexi about this page
      </div>

      {error && (
        <div className="error-state">
          <strong>Error:</strong> {error}
        </div>
      )}

      <div className="factories-table-container">
        {/* Filter Toggle and Card */}
        <div className="factories-filter-section">
          <div className="filter-section">
            <div className="filter-toggle-header">
              <button
                className="filter-toggle-btn"
                onClick={() => setShowFilters(!showFilters)}
              >
                {showFilters ? '▼' : '▶'} Filter Factories
                {hasActiveFilters && <span className="active-filter-indicator">({Object.values(columnFilters).filter(filter => filter && filter !== 'all').length} active)</span>}
              </button>
              {hasActiveFilters && (
                <button className="clear-filters-btn" onClick={clearAllFilters}>
                  Clear All Filters
                </button>
              )}
            </div>

            {showFilters && (
              <div className="table-filters">
                <div className="filters-grid">
                  <FilterInput
                    label="Factory Name"
                    placeholder="Search by factory name..."
                    column="name"
                    value={columnFilters.name}
                    onChange={handleColumnFilterChange}
                  />
                  <FilterInput
                    label="Address"
                    placeholder="Search by address..."
                    column="address"
                    value={columnFilters.address}
                    onChange={handleColumnFilterChange}
                  />
                  <FilterInput
                    label="City"
                    placeholder="Search by city..."
                    column="city"
                    value={columnFilters.city}
                    onChange={handleColumnFilterChange}
                  />
                  <FilterInput
                    label="State/Province"
                    placeholder="Search by state/province..."
                    column="state_province"
                    value={columnFilters.state_province}
                    onChange={handleColumnFilterChange}
                  />
                  <FilterInput
                    label="Country"
                    placeholder="Search by country..."
                    column="country"
                    value={columnFilters.country}
                    onChange={handleColumnFilterChange}
                  />
                  <FilterInput
                    label="Postal Code"
                    placeholder="Search by postal code..."
                    column="postal_code"
                    value={columnFilters.postal_code}
                    onChange={handleColumnFilterChange}
                  />
                  <FilterInput
                    label="Phone"
                    placeholder="Search by phone..."
                    column="phone"
                    value={columnFilters.phone}
                    onChange={handleColumnFilterChange}
                  />
                  <FilterInput
                    label="Email"
                    placeholder="Search by email..."
                    column="email"
                    value={columnFilters.email}
                    onChange={handleColumnFilterChange}
                  />
                  <div className="filter-group">
                    <label className="filter-label">Status</label>
                    <select
                      className="filter-select"
                      value={columnFilters.status}
                      onChange={(e) => handleColumnFilterChange('status', e.target.value)}
                    >
                      <option value="all">All Status</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Table Controls */}
        <div className="factories-table-header-controls">
          <button className="add-data-btn" onClick={openAddModal}>
            + Add Factory
          </button>
          <button className="export-csv-btn" onClick={handleExportCSV}>
            Export CSV
          </button>
        </div>

        {/* Factory Table */}
        <div className="factories-table-wrapper">
          <div className="factories-table-scrollable">
            <div className="factories-data-table">
              <div className="table-header">
                <div className="factories-table-row">
                  {tableColumns.filter(col => col.showInTable).map((column) => (
                    <TableHeaderCell key={column.key} column={column} />
                  ))}
                </div>
              </div>

              {sortedFactories.length === 0 ? (
                <div className="factories-table-row">
                  <div className="empty-table-message">
                    {hasActiveFilters ? (
                      <>
                        <h4>No factories found</h4>
                        <p>Try adjusting your filter criteria.</p>
                      </>
                    ) : (
                      <p style={{ color: '#999', fontStyle: 'italic' }}>No factories yet</p>
                    )}
                  </div>
                </div>
              ) : (
                paginatedFactories.map((factory: Factory) => (
                  <div key={factory.id} className="factories-table-row">
                    {tableColumns.filter(col => col.showInTable).map((column) => (
                      <TableDataCell key={column.key} column={column} factory={factory} />
                    ))}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Pagination */}
        <SmartPagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          itemType="factories"
          onPageChange={handlePageChange}
        />
      </div>

      {/* Help Popup */}
      {popupState.help && (
        <RexiPopup
          message="Factories are the manufacturing locations where your products are made. This information is required for CPSC eFiling and helps track the origin of your certified products. Keep factory information current and accurate for compliance."
          onClose={() => togglePopup('help', false)}
          confirmText="Got It"
        />
      )}

      {/* Delete Confirmation Popup */}
      {popupState.delete && factoryToDelete && (
        <RexiPopup
          message="Are you sure you want to delete this record? This action cannot be undone."
          onClose={() => {
            togglePopup('delete', false);
            setFactoryToDelete(null);
          }}
          onContinue={handleDeleteFactory}
          showCancel={true}
        />
      )}

      {/* Foreign Key Constraint Error Popup */}
      {popupState.foreignKeyError && (
        <RexiPopup
          message="This factory cannot be deleted because it is currently being used in one or more test reports. To delete this factory, you must first remove or reassign all test reports that reference this factory."
          onClose={() => togglePopup('foreignKeyError', false)}
          confirmText="Understood"
        />
      )}

      {/* Add/Edit Factory Modal */}

      <AddEditFactoryModal
        isOpen={popupState.modal}
        onClose={closeModal}
        editingFactory={editingFactory}
        formData={formData}
        formErrors={formErrors}
        saving={saving}
        handleInputChange={handleInputChange}
        handleSaveFactory={handleSaveFactory}
      />

    </div>
  );
};

export default Factories;